import type {
  OrderProductRealizationDto,
  OrderTransportsDto,
  RouteOrderDto,
  TransportCargoItemDto,
  TransportOrderSummaryDto,
  TransportProductDto,
  TransportRouteDetailDto,
  TransportRouteStatus,
  TransportRouteSummaryDto,
} from '../types/transport';

import {
  asArray,
  asNumber,
  asOptionalArray,
  asOptionalString,
  asRecord,
  asString,
  reportDtoAnomaly,
} from './common';
import { normalizeCustomer, normalizeUser } from './order';
import { getDisplayOrderNumber } from '../types/order';
import { normalizeTransportAddressList } from '../utils/transportAddressUtils';
import {
  TRANSPORT_COST_ALLOCATION_METHODS,
  type AllocationMethod as TransportCostAllocationMethod,
  type TransportCostAllocationResult,
  type TransportCostAllocationWarningCode,
} from '../utils/transportCostAllocation';

export type {
  OrderProductRealizationDto,
  OrderTransportsDto,
  RouteOrderDto,
  TransportCargoItemDto,
  TransportOrderSummaryDto,
  TransportProductDto,
  TransportRouteDetailDto,
  TransportRouteStatus,
  TransportRouteSummaryDto,
} from '../types/transport';

const ROUTE_STATUSES: TransportRouteStatus[] = [
  'PENDING',
  'SCHEDULED',
  'LOADING',
  'IN_TRANSIT',
  'DELIVERED',
  'CANCELLED',
  'PLANNED',
  'DELAYED',
];

export function normalizeTransportRouteSummary(
  raw: unknown,
  entity = 'transportRoute',
): TransportRouteSummaryDto {
  const record = asRecord(raw);

  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }

  const routeOrders = normalizeRouteOrders(record?.routeOrders, `${entity}.routeOrders`);
  const fallbackOrder = normalizeRouteOrderFallback(record, `${entity}.fallbackOrder`);
  const effectiveRouteOrders = routeOrders.length > 0 ? routeOrders : fallbackOrder ? [fallbackOrder] : [];
  const firstOrder = effectiveRouteOrders[0]?.order;
  const products = normalizeTransportProducts(record?.products, `${entity}.products`);

  return {
    id: asString(record?.id),
    routeNumber: asString(record?.routeNumber),
    originCity: asString(record?.originCity),
    originCountry: asString(record?.originCountry),
    destinationCity: asString(record?.destinationCity),
    destinationCountry: asString(record?.destinationCountry),
    departureDate: asString(record?.departureDate),
    estimatedArrival: asString(record?.estimatedArrival),
    actualArrival: asOptionalString(record?.actualArrival),
    transportCompany: asString(record?.transportCompany),
    transportCompanyId: asOptionalString(record?.transportCompanyId) ?? null,
    transportContact: asOptionalString(record?.transportContact),
    vehicleRegistration: asOptionalString(record?.vehicleRegistration),
    driverName: asOptionalString(record?.driverName),
    driverPhone: asOptionalString(record?.driverPhone),
    transportCost: asNumber(record?.transportCost),
    transportRate: record?.transportRate == null ? undefined : asNumber(record?.transportRate),
    currency: asString(record?.currency, 'PLN'),
    costAllocationMethod: normalizeTransportCostAllocationMethod(record?.costAllocationMethod),
    costAllocation: normalizeTransportCostAllocation(record?.costAllocation, `${entity}.costAllocation`),
    invoiceNumber: asOptionalString(record?.invoiceNumber),
    status: normalizeTransportStatus(record?.status, `${entity}.status`),
    notes: asOptionalString(record?.notes),
    orderId: asOptionalString(record?.orderId) || effectiveRouteOrders[0]?.orderId,
    orderNumber: asOptionalString(record?.orderNumber) || firstOrder?.orderNumber || '',
    displayOrderNumber:
      asOptionalString(record?.displayOrderNumber)
      || firstOrder?.displayOrderNumber
      || asOptionalString(record?.orderNumber)
      || firstOrder?.orderNumber
      || '',
    customerName: pickTransportCustomerName(record, firstOrder),
    customer: asOptionalString(record?.customer),
    products: products.length > 0 ? products : firstOrder?.products || [],
    routeOrders: effectiveRouteOrders,
    cargoItems: normalizeTransportCargoItems(record?.cargoItems, `${entity}.cargoItems`),
    loadingDate: asOptionalString(record?.loadingDate),
    estimatedDelivery: asOptionalString(record?.estimatedDelivery),
    actualDelivery: asOptionalString(record?.actualDelivery),
    loadingAddresses: normalizeTransportAddressList(record?.loadingAddresses),
    deliveryAddresses: normalizeTransportAddressList(record?.deliveryAddresses),
    dimensions: asOptionalString(record?.dimensions),
    licensePlate1: asOptionalString(record?.licensePlate1),
    licensePlate2: asOptionalString(record?.licensePlate2),
    totalWeight: record?.totalWeight == null ? undefined : asNumber(record?.totalWeight),
    totalVolume: record?.totalVolume == null ? undefined : asNumber(record?.totalVolume),
    packages: record?.packages == null ? undefined : asNumber(record?.packages),
    createdAt: asString(record?.createdAt),
    updatedAt: asString(record?.updatedAt),
  };
}

export function normalizeTransportRoutes(
  raw: unknown,
  entity = 'transportRoutes',
): TransportRouteSummaryDto[] {
  return asArray(raw, (item, index) => normalizeTransportRouteSummary(item, `${entity}[${index}]`));
}

export function normalizeTransportRouteDetail(
  raw: unknown,
  entity = 'transportRouteDetail',
): TransportRouteDetailDto {
  const summary = normalizeTransportRouteSummary(raw, entity);
  const record = asRecord(raw);
  const createdBy = normalizeUser(record?.createdBy, `${entity}.createdBy`);

  return {
    ...summary,
    createdByName: createdBy?.name || '',
    createdByEmail: createdBy?.email || '',
  };
}

export function normalizeOrderTransports(
  raw: unknown,
  entity = 'orderTransports',
): OrderTransportsDto {
  const record = asRecord(raw);

  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }

  return {
    transports: asArray(record?.transports, (item, index) =>
      normalizeTransportRouteDetail(item, `${entity}.transports[${index}]`),
    ),
    productRealization: asArray(record?.productRealization, (item, index) =>
      normalizeOrderProductRealization(item, `${entity}.productRealization[${index}]`),
    ),
  };
}

function normalizeOrderProductRealization(
  raw: unknown,
  entity: string,
): OrderProductRealizationDto {
  const record = asRecord(raw);

  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }

  return {
    orderProductId: asString(record?.orderProductId),
    name: asString(record?.name),
    unit: asOptionalString(record?.unit) ?? null,
    orderedQuantity: asNumber(record?.orderedQuantity),
    allocatedQuantity: asNumber(record?.allocatedQuantity),
    remainingQuantity: asNumber(record?.remainingQuantity),
    percent: asNumber(record?.percent),
  };
}

function normalizeRouteOrders(raw: unknown, entity: string): RouteOrderDto[] {
  return asArray(raw, (item, index) => {
    const record = asRecord(item);
    const order = normalizeTransportOrderSummary(record?.order, `${entity}[${index}].order`);

    return {
      id: asString(record?.id) || asString(record?.orderId) || order.id,
      orderId: asString(record?.orderId) || order.id,
      allocationMethod: asOptionalString(record?.allocationMethod) ?? undefined,
      allocationBasisValue: record?.allocationBasisValue == null ? undefined : asNumber(record?.allocationBasisValue),
      allocationPercent: record?.allocationPercent == null ? undefined : asNumber(record?.allocationPercent),
      allocatedTransportCost: record?.allocatedTransportCost == null ? undefined : asNumber(record?.allocatedTransportCost),
      // P8: null/undefined means "no conversion happened" (plain-PLN
      // transport) — never coerce to a guessed currency.
      originalCurrency: asOptionalString(record?.originalCurrency) ?? null,
      originalAmount: record?.originalAmount == null ? null : asNumber(record?.originalAmount),
      exchangeRateUsed: record?.exchangeRateUsed == null ? null : asNumber(record?.exchangeRateUsed),
      cargoDescription: asOptionalString(record?.cargoDescription),
      packages: record?.packages == null ? undefined : asNumber(record?.packages),
      weight: record?.weight == null ? undefined : asNumber(record?.weight),
      volume: record?.volume == null ? undefined : asNumber(record?.volume),
      areaSqm: record?.areaSqm == null ? undefined : asNumber(record?.areaSqm),
      ldm: record?.ldm == null ? undefined : asNumber(record?.ldm),
      order,
    };
  });
}

function normalizeRouteOrderFallback(record: ReturnType<typeof asRecord>, entity: string): RouteOrderDto | null {
  if (!record || !record.orderId) {
    return null;
  }

  return {
    id: asString(record.orderId),
    orderId: asString(record.orderId),
    order: normalizeTransportOrderSummary(record, `${entity}.order`),
  };
}

function normalizeTransportOrderSummary(raw: unknown, entity: string): TransportOrderSummaryDto {
  const record = asRecord(raw);
  const customer = normalizeCustomer(record?.customer, `${entity}.customer`);
  const orderNumber = asString(record?.orderNumber);
  const manualOrderNumber = asOptionalString(record?.manualOrderNumber) ?? null;
  const useManualNumbering = record?.useManualNumbering === true;

  return {
    id: asString(record?.id),
    orderNumber,
    manualOrderNumber,
    useManualNumbering,
    displayOrderNumber:
      asOptionalString(record?.displayOrderNumber)
      || getDisplayOrderNumber({ orderNumber, manualOrderNumber, useManualNumbering }),
    clientOrderNumber: asOptionalString(record?.clientOrderNumber) ?? null,
    customerName: customer?.name || asOptionalString(record?.customerName) || '',
    revenue: asNumber(record?.revenue),
    currency: asString(record?.currency, 'PLN'),
    status: asString(record?.status),
    products: normalizeTransportProducts(record?.products, `${entity}.products`),
    customer,
  };
}

function normalizeTransportProducts(raw: unknown, entity: string): TransportProductDto[] {
  return asArray(raw, (item, index) => {
    const record = asRecord(item);

    return {
      id: asString(record?.id),
      cargoItemId: asOptionalString(record?.cargoItemId),
      orderId: asOptionalString(record?.orderId),
      name: asString(record?.name),
      quantity: record?.quantity == null ? undefined : asNumber(record?.quantity),
      unit: asOptionalString(record?.unit),
      grossWeight: record?.grossWeight == null ? undefined : asNumber(record?.grossWeight),
      width: record?.width == null ? undefined : asNumber(record?.width),
      height: record?.height == null ? undefined : asNumber(record?.height),
      length: record?.length == null ? undefined : asNumber(record?.length),
      thickness: record?.thickness == null ? undefined : asNumber(record?.thickness),
      productCategoryDimensionDisplayProfile:
        asOptionalString(record?.productCategoryDimensionDisplayProfile),
    };
  });
}

function normalizeTransportCargoItems(raw: unknown, entity: string): TransportCargoItemDto[] {
  return asArray(raw, (item, index) => {
    const record = asRecord(item);

    return {
      id: asString(record?.id),
      transportId: asString(record?.transportId),
      orderId: asString(record?.orderId),
      orderProductId: asOptionalString(record?.orderProductId),
      productName: asString(record?.productName),
      quantity: asNumber(record?.quantity),
      unit: asOptionalString(record?.unit),
      goodsValueTotal: record?.goodsValueTotal == null ? null : asNumber(record?.goodsValueTotal),
      manualAllocatedTransportCost: record?.manualAllocatedTransportCost == null
        ? null
        : asNumber(record?.manualAllocatedTransportCost),
      width: record?.width == null ? undefined : asNumber(record?.width),
      height: record?.height == null ? undefined : asNumber(record?.height),
      length: record?.length == null ? undefined : asNumber(record?.length),
      thickness: record?.thickness == null ? undefined : asNumber(record?.thickness),
      grossWeight: record?.grossWeight == null ? undefined : asNumber(record?.grossWeight),
      // Package-based LDM snapshot (migration 093). Null stays null so an
      // absent packaging value renders "—" rather than a fabricated 0.
      piecesPerPackage: record?.piecesPerPackage == null ? null : asNumber(record?.piecesPerPackage),
      packageLength: record?.packageLength == null ? null : asNumber(record?.packageLength),
      packageWidth: record?.packageWidth == null ? null : asNumber(record?.packageWidth),
      packageStackLayers: record?.packageStackLayers == null ? null : asNumber(record?.packageStackLayers),
      // F2 (migration 094): manual LDM override. Null stays null so an absent
      // override falls back to packaging rather than a fabricated 0.
      manualLdm: record?.manualLdm == null ? null : asNumber(record?.manualLdm),
    };
  });
}

function normalizeTransportCostAllocation(
  raw: unknown,
  entity: string,
): TransportCostAllocationResult | undefined {
  if (raw == null) {
    return undefined;
  }

  const record = asRecord(raw);
  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }

  return {
    method: normalizeTransportCostAllocationMethod(record?.method),
    items: normalizeTransportCostAllocationItems(record?.items, `${entity}.items`),
    summary: {
      totalGoodsValue: asNumber(asRecord(record?.summary)?.totalGoodsValue),
      totalAllocatedTransportCost: asNumber(asRecord(record?.summary)?.totalAllocatedTransportCost),
      totalLandedCost: asNumber(asRecord(record?.summary)?.totalLandedCost),
      allocationDifference: asNumber(asRecord(record?.summary)?.allocationDifference),
    },
    warnings: asArray(record?.warnings, (item, index) => {
      const warning = asRecord(item);
      const code = asString(warning?.code) as TransportCostAllocationWarningCode;
      // MISSING_DIMENSIONS is an aggregated warning carrying the offending
      // lines' itemId/name in warning.items[]; the detail page's renderer
      // shows them as a "Pozycje: <names>" suffix, same as the form. Dropping
      // this field silently emptied that suffix on TransportRouteDetail.
      const items = asOptionalArray(warning?.items, (warningItem) => {
        const warningItemRecord = asRecord(warningItem);
        return {
          itemId: asOptionalString(warningItemRecord?.itemId),
          name: asOptionalString(warningItemRecord?.name) ?? null,
        };
      });

      return {
        code,
        itemId: asOptionalString(warning?.itemId),
        ...(items ? { items } : {}),
      };
    }),
  };
}

function normalizeTransportCostAllocationItems(
  raw: unknown,
  entity: string,
): TransportCostAllocationResult['items'] {
  const rawItems = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? Object.values(raw) : [];

  return asArray(rawItems, (item, index) => {
    const record = asRecord(item);
    if (!record) {
      reportDtoAnomaly(`${entity}[${index}]`, 'root', item);
    }

    return {
      id: asOptionalString(record?.id) || asOptionalString(record?.itemId),
      quantity: asNumber(record?.quantity),
      unit: asOptionalString(record?.unit) ?? null,
      goodsValueTotal: asNumber(record?.goodsValueTotal),
      manualAllocatedTransportCost: asNumber(record?.manualAllocatedTransportCost),
      allocatedTransportCost: asNumber(record?.allocatedTransportCost),
      goodsCostPerUnit: record?.goodsCostPerUnit == null ? null : asNumber(record?.goodsCostPerUnit),
      transportCostPerUnit: record?.transportCostPerUnit == null ? null : asNumber(record?.transportCostPerUnit),
      landedCostTotal: asNumber(record?.landedCostTotal),
      landedCostPerUnit: record?.landedCostPerUnit == null ? null : asNumber(record?.landedCostPerUnit),
      // OSCAR (PDF-contract wave): per-metric unit costs computed server-side
      // by buildPerMetricUnitCosts (transportCostAllocation.js) — null stays
      // null (metric not computable for this line), never coerced to 0.
      transportCostPerAreaSqm: record?.transportCostPerAreaSqm == null ? null : asNumber(record?.transportCostPerAreaSqm),
      transportCostPerVolumeM3: record?.transportCostPerVolumeM3 == null ? null : asNumber(record?.transportCostPerVolumeM3),
      transportCostPerLdm: record?.transportCostPerLdm == null ? null : asNumber(record?.transportCostPerLdm),
    };
  });
}

function normalizeTransportCostAllocationMethod(value: unknown): TransportCostAllocationMethod {
  return TRANSPORT_COST_ALLOCATION_METHODS.includes(value as TransportCostAllocationMethod)
    ? value as TransportCostAllocationMethod
    : 'BY_COMMERCIAL_QTY';
}

function normalizeTransportStatus(value: unknown, entity: string): TransportRouteStatus {
  if (typeof value === 'string' && ROUTE_STATUSES.includes(value as TransportRouteStatus)) {
    return value as TransportRouteStatus;
  }

  reportDtoAnomaly(entity, 'value', value);
  return 'PLANNED';
}

function pickTransportCustomerName(
  record: ReturnType<typeof asRecord>,
  firstOrder: TransportOrderSummaryDto | undefined,
): string {
  if (!record) {
    return firstOrder?.customerName || '';
  }

  const rawCustomer = record.customer;

  if (typeof rawCustomer === 'string' && rawCustomer.trim()) {
    return rawCustomer;
  }

  return asOptionalString(record.customerName) || firstOrder?.customerName || '';
}
